import { Component, DestroyRef, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { Order } from '../../../overview/models/order';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { DetailsComponent } from './components/details/details.component';
import { ItemsComponent } from './components/items/items.component';
import { OrderExceptionTableComponent } from '../../../order-exceptions/components/order-exception-table/order-exception-table.component';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { catchError, NEVER, tap } from 'rxjs';
import { OrderDetailsService } from './services/order-details.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-order-details',
  standalone: true,
  templateUrl: './order-details.component.html',
  imports: [
    MatTab,
    MatTabGroup,
    MatTabLabel,
    DetailsComponent,
    ItemsComponent,
    OrderExceptionTableComponent,
    MatTooltip,
    NgClass
  ],
  styleUrl: './order-details.component.scss'
})
export class OrderDetailsComponent {
  @Input() orderId: string | null = null;
  @Input() isArchived = false;
  @Output() detailsClosed = new EventEmitter();
  isBeingWatched = false;
  order: Order | null = null;

  private orderDetailsService = inject(OrderDetailsService);
  private destroyRef = inject(DestroyRef);
  private matSnackBar = inject(MatSnackBar);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderId'] && changes['orderId'].currentValue) {
      this.order = null;
      this.orderDetailsService
        .getOrderBy(this.orderId!, this.isArchived)
        .pipe(
          tap(order => {
            this.order = order;
            this.isBeingWatched = order.watched;
          }),
          catchError(error => {
            this.matSnackBar.open('Could not get the order details. Please try again.', 'x');
            console.error(error);
            return NEVER;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  toggleWatch() {
    this.orderDetailsService
      .watched(this.orderId!, !this.isBeingWatched)
      .pipe(
        tap(() => {
          if (this.isBeingWatched) {
            this.handleSuccess('Order removed from watch list');
          } else {
            this.handleSuccess('Order added to watch list');
          }
          this.isBeingWatched = !this.isBeingWatched;
        }),
        catchError(error => this.handleError(error)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private handleSuccess(message: string) {
    this.matSnackBar.open(message, 'x', { panelClass: 'snackbar-green' });
  }

  private handleError(error: HttpErrorResponse) {
    this.matSnackBar.open('Could not update order watch status, please try again later.', 'x');
    console.error(error);

    return NEVER;
  }
}
